/**
 * NotificationsInboxDetailController — panneau « Email du moment » de
 * /notifications.html (EPIC-033 / 33.6).
 *
 * Écoute le CustomEvent `notifications:inbox-item-selected` dispatché par
 * {@see NotificationsInboxController} et charge le détail via
 * GET /me/notifications/{id} (corps HTML rendu + historique events
 * delivery).
 *
 * Le corps HTML rendu est isolé dans une `<iframe sandbox>` (sans
 * `allow-scripts`) — l'origine du HTML est la DB Notamicro mais le
 * sandbox supprime tout risque résiduel d'exécution de script.
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS, l'iframe est
 * créée par `document.createElement` (pas d'innerHTML), les lignes
 * d'events sont clonées depuis `<template id="tpl-inbox-detail-event">`.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const EVENT_TYPE_LABELS = {
  delivered: "Livré",
  opened: "Ouvert",
  bounced: "Rebond",
  failed: "Échec",
};

export default class NotificationsInboxDetailController extends BaseController {
  static targets = [
    "meta",
    "subject",
    "body",
    "eventsList",
    "eventsEmpty",
    "placeholder",
    "content",
  ];

  connect() {
    this._listener = (event) => {
      const id = event?.detail?.id;
      if (id) this._loadDetail(String(id));
    };
    window.addEventListener("notifications:inbox-item-selected", this._listener);
  }

  disconnect() {
    if (this._listener) {
      window.removeEventListener("notifications:inbox-item-selected", this._listener);
    }
  }

  async _loadDetail(id) {
    try {
      const data = await api.get(`/me/notifications/${encodeURIComponent(id)}`);
      this._render(data);
    } catch (error) {
      console.warn("[notifications-inbox-detail] load failed", error);
      this.showError("Impossible de charger cet email.");
    }
  }

  _render(data) {
    const notif = (data && data.notification) || {};
    const events = Array.isArray(data?.deliveryEvents) ? data.deliveryEvents : [];

    if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = true;
    if (this.hasContentTarget) this.contentTarget.hidden = false;

    if (this.hasMetaTarget) {
      const sentAt = notif.sentAt ? this.formatDate(notif.sentAt) : "—";
      this.metaTarget.textContent = `De : Notamicro · à ${notif.recipientEmail || "—"} · ${sentAt} · statut ${this._statusLabel(notif.status)}`;
    }
    if (this.hasSubjectTarget) {
      this.subjectTarget.textContent = notif.subjectSnapshot || "(sans objet)";
    }
    if (this.hasBodyTarget) {
      while (this.bodyTarget.firstChild) this.bodyTarget.removeChild(this.bodyTarget.firstChild);
      if (notif.bodyHtml) {
        const iframe = document.createElement("iframe");
        iframe.title = "Corps de l'email";
        iframe.setAttribute("sandbox", "");
        iframe.style.width = "100%";
        iframe.style.minHeight = "320px";
        iframe.style.border = "0";
        iframe.srcdoc = String(notif.bodyHtml);
        this.bodyTarget.appendChild(iframe);
      } else {
        const fallback = document.createElement("p");
        fallback.className = "fs-13 text-muted";
        fallback.style.margin = "0";
        fallback.textContent = "Le corps de cet email n'est plus disponible.";
        this.bodyTarget.appendChild(fallback);
      }
    }
    if (this.hasEventsListTarget) {
      while (this.eventsListTarget.firstChild) {
        this.eventsListTarget.removeChild(this.eventsListTarget.firstChild);
      }
      if (this.hasEventsEmptyTarget) {
        this.eventsEmptyTarget.hidden = events.length !== 0;
      }
      for (const ev of events) {
        const fragment = this.cloneTemplate("tpl-inbox-detail-event");
        if (!fragment) continue;
        this.fillSlots(fragment, {
          eventType: EVENT_TYPE_LABELS[ev.eventType] || ev.eventType || "—",
          receivedAt: ev.receivedAt ? this.formatDate(ev.receivedAt) : "—",
        });
        this.eventsListTarget.appendChild(fragment);
      }
    }
  }

  _statusLabel(status) {
    return {
      queued: "en attente",
      sent: "envoyé",
      delivered: "livré",
      opened: "ouvert",
      bounced: "rebond",
      failed: "échec",
    }[status] || (status || "—");
  }
}
